import React, { useState, useEffect, useRef } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';

const Testimonials = ({ testimonials = [], isLoading = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const autoplayRef = useRef(null);
  const [imageErrors, setImageErrors] = useState({});

  // Handle autoplay functionality
  useEffect(() => {
    if (autoplay && testimonials.length > 0) {
      autoplayRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000); // Change testimonial every 5 seconds
    }

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [autoplay, testimonials.length]);

  // Pause autoplay on hover
  const handleMouseEnter = () => setAutoplay(false);
  const handleMouseLeave = () => setAutoplay(true);

  // Navigate to a specific testimonial
  const goToTestimonial = (index) => {
    setCurrentIndex(index);
    // Reset autoplay timer when manually navigating
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
    if (autoplay) {
      autoplayRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      }, 5000);
    }
  };

  // Handle image error
  const handleImageError = (testimonialId) => {
    setImageErrors(prev => ({
      ...prev,
      [testimonialId]: true
    }));
  };

  // Get fallback avatar based on name
  const getFallbackAvatar = (name) => {
    // Use consistent avatars from UI Faces
    const fallbackAvatars = [
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&h=150&q=80',
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&h=150&q=80',
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&h=150&q=80',
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&h=150&q=80',
      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=150&h=150&q=80'
    ];
    
    // Use the first character of the name to determine which avatar to use
    const charCode = name.charCodeAt(0) || 0;
    const index = charCode % fallbackAvatars.length;
    return fallbackAvatars[index];
  };

  // Render stars based on rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <StarIcon
        key={index}
        className={`h-5 w-5 ${
          index < rating ? 'text-yellow-500' : 'text-gray-300'
        }`}
      />
    ));
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 animate-pulse">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-gray-300 rounded-full"></div>
          </div>
          <div className="flex justify-center mb-4">
            <div className="h-5 bg-gray-300 rounded w-1/3"></div>
          </div>
          <div className="h-4 bg-gray-300 rounded w-full mb-3"></div>
          <div className="h-4 bg-gray-300 rounded w-full mb-3"></div>
          <div className="h-4 bg-gray-300 rounded w-3/4 mb-6"></div>
          <div className="flex justify-center">
            <div className="h-5 bg-gray-300 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // No testimonials available
  if (!testimonials || testimonials.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No testimonials available at the moment.</p>
      </div>
    );
  }

  return (
    <div 
      className="max-w-4xl mx-auto testimonials-container" 
      onMouseEnter={handleMouseEnter} 
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative">
        {/* Testimonial Carousel */}
        <div className="overflow-hidden testimonial-carousel">
          <div 
            className="transition-transform duration-500 ease-in-out testimonial-slider"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id} 
                  className="w-full flex-shrink-0 bg-white rounded-lg shadow-md p-8 testimonial-slide"
                >
                  {/* Avatar */}
                  <div className="flex justify-center mb-6">
                    <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-200">
                      <img 
                        src={imageErrors[testimonial.id] ? getFallbackAvatar(testimonial.name) : (testimonial.avatar || getFallbackAvatar(testimonial.name))}
                        alt={testimonial.name} 
                        className="h-full w-full object-cover"
                        onError={() => handleImageError(testimonial.id)}
                        loading="eager"
                      />
                    </div>
                  </div>
                  
                  {/* Rating */}
                  <div className="flex justify-center mb-4">
                    {renderStars(testimonial.rating)}
                  </div>
                  
                  {/* Testimonial Text */}
                  <p className="text-gray-600 text-center mb-6 italic">
                    "{testimonial.comment}"
                  </p>
                  
                  {/* Customer Name */}
                  <h4 className="text-center font-bold text-lg">
                    {testimonial.name}
                    {testimonial.tour_name && (
                      <span className="block text-sm font-normal text-gray-500 mt-1">
                        {testimonial.tour_name}
                      </span>
                    )}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Navigation Dots */}
        <div className="flex justify-center mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`h-3 w-3 mx-1 rounded-full transition-colors duration-300 ${
                index === currentIndex ? 'bg-primary' : 'bg-gray-300'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
